import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import en from '@/locales/en.json';
import arAE from '@/locales/ar-AE.json';
import he from '@/locales/he.json';

type Locale = 'en' | 'ar-AE' | 'he';
type Direction = 'ltr' | 'rtl';

interface I18nContextType {
  locale: Locale;
  direction: Direction;
  t: (key: string, params?: Record<string, string>) => string;
  setLocale: (locale: Locale) => void;
}

const translations: Record<Locale, any> = {
  'en': en,
  'ar-AE': arAE,
  'he': he,
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const STORAGE_KEY = 'savoy.lang';

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isImpersonating, setIsImpersonating] = useState(() => {
    // Check sessionStorage only (tab-isolated)
    return !!sessionStorage.getItem('impersonation_mode');
  });
  
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [initialDirectionSet, setInitialDirectionSet] = useState(false);
  
  const [locale, setLocaleState] = useState<Locale>(() => {
    // Check for impersonation mode first
    const impersonationMode = sessionStorage.getItem('impersonation_mode');
    if (impersonationMode) {
      // During impersonation, always use English (LTR) for preview
      return 'en';
    }
    
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'ar-AE' || stored === 'he' || stored === 'en') {
      return stored as Locale;
    }
    return 'en';
  });

  // Force LTR for admins (impersonation, platform admin, or brand owner)
  const forceAdminLTR = isImpersonating || isPlatformAdmin || isOwner;
  const direction: Direction = forceAdminLTR ? 'ltr' : (locale === 'en' ? 'ltr' : (locale === 'ar-AE' || locale === 'he') ? 'rtl' : 'ltr');
  
  // Set initial HTML direction immediately to prevent flickering
  useEffect(() => {
    if (!initialDirectionSet) {
      const initialDir = forceAdminLTR ? 'ltr' : (locale === 'en' ? 'ltr' : 'rtl');
      document.documentElement.dir = initialDir;
      document.documentElement.lang = locale;
      
      if (initialDir === 'rtl') {
        document.body.classList.add('rtl');
      } else {
        document.body.classList.remove('rtl');
      }
      
      setInitialDirectionSet(true);
      console.log('🌐 Initial direction set:', initialDir, 'Locale:', locale);
    }
  }, []); // Run once on mount
  
  // Monitor sessionStorage and localStorage for admin status changes
  useEffect(() => {
    const checkAdminStatus = async () => {
      // Check sessionStorage only (tab-isolated)
      const newIsImpersonating = !!sessionStorage.getItem('impersonation_mode');
      
      if (newIsImpersonating !== isImpersonating) {
        console.log('🎭 Impersonation mode changed:', newIsImpersonating);
        setIsImpersonating(newIsImpersonating);
        if (newIsImpersonating) {
          setLocaleState('en');
        }
      }
      
      // Check platform admin role from session cache
      const platformRoleCache = sessionStorage.getItem('savoy.platform_role_cache');
      if (platformRoleCache) {
        try {
          const parsed = JSON.parse(platformRoleCache);
          const newIsPlatformAdmin = parsed.isPlatformAdmin || false;
          if (newIsPlatformAdmin !== isPlatformAdmin) {
            console.log('👑 Platform admin status changed:', newIsPlatformAdmin);
            setIsPlatformAdmin(newIsPlatformAdmin);
            if (newIsPlatformAdmin) {
              setLocaleState('en'); // Force English for platform admins
            }
          }
        } catch (e) {
          // Invalid cache, ignore
        }
      }
      
      // Check if user is owner from role cache
      const userRoleCache = localStorage.getItem('savoy.user_role_cache');
      if (userRoleCache) {
        const newIsOwner = userRoleCache === 'owner';
        if (newIsOwner !== isOwner) {
          console.log('👤 Owner status changed:', newIsOwner);
          setIsOwner(newIsOwner);
          if (newIsOwner) {
            setLocaleState('en'); // Force English for brand owners
          }
        }
      }
    };
    
    // Check immediately
    checkAdminStatus();
    
    // Check periodically (fallback for navigation changes)
    const interval = setInterval(checkAdminStatus, 500);
    
    return () => clearInterval(interval);
  }, [isImpersonating, isPlatformAdmin, isOwner]);

  useEffect(() => {
    // Skip if initial direction hasn't been set yet
    if (!initialDirectionSet) return;
    
    // Update HTML attributes - enforce LTR for all admin types
    if (forceAdminLTR) {
      document.documentElement.lang = 'en';
      document.documentElement.dir = 'ltr';
      document.body.classList.remove('rtl');
      
      const adminType = isImpersonating ? 'Preview' : isPlatformAdmin ? 'Platform Admin' : 'Brand Owner';
      console.log(`👑 ${adminType} mode: Forcing LTR direction`);
    } else {
      document.documentElement.lang = locale;
      document.documentElement.dir = direction;
      
      // Force remove RTL class for English
      if (locale === 'en') {
        document.body.classList.remove('rtl');
        document.documentElement.dir = 'ltr';
      } else if (direction === 'rtl') {
        document.body.classList.add('rtl');
      } else {
        document.body.classList.remove('rtl');
      }
    }

    // Set cookie for server-side usage
    document.cookie = `lang=${locale}; path=/; max-age=31536000; SameSite=Lax`;
  }, [locale, direction, forceAdminLTR, isImpersonating, isPlatformAdmin, isOwner, initialDirectionSet]);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem(STORAGE_KEY, newLocale);
    
    // Announce language change for screen readers
    const announcements = {
      'en': 'Language changed to English',
      'ar-AE': 'تم تغيير اللغة إلى العربية',
      'he': 'השפה שונתה לעברית'
    };
    const announcement = announcements[newLocale];
    const announcer = document.createElement('div');
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', 'polite');
    announcer.className = 'sr-only';
    announcer.textContent = announcement;
    document.body.appendChild(announcer);
    setTimeout(() => document.body.removeChild(announcer), 1000);
  };

  const t = (key: string, params?: Record<string, string>): string => {
    const keys = key.split('.');
    let value: any = translations[locale];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        console.warn(`Translation key not found: ${key} for locale ${locale}`);
        return key;
      }
    }
    
    if (typeof value !== 'string') {
      console.warn(`Translation key ${key} is not a string for locale ${locale}`);
      return key;
    }
    
    // Replace parameters
    if (params) {
      Object.entries(params).forEach(([param, replacement]) => {
        value = value.replace(new RegExp(`{{${param}}}`, 'g'), replacement);
      });
    }
    
    return value;
  };

  return (
    <I18nContext.Provider value={{ locale, direction, t, setLocale }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
